<template>
  <div class="read-more-wrapper">
    <div
      class="read-more-text"
      :class="{ 'text-truncated': !expanded }"
      :style="contentStyle"
      ref="contentRef"
    >
      <div class="content" ref="innerContentRef">
        <slot></slot>
      </div>
      <div class="gradient-fade" :class="{ 'fade-out': expanded }"></div>
    </div>

    <v-btn
      variant="outlined"
      color="lighter-grey"
      class="read-more-btn mt-2"
      @click="toggleExpanded"
    >
      <span class="text-secondary">
        {{ expanded ? 'Read Less' : 'Read More' }}</span
      >
      <template v-slot:append>
        <v-icon
          :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          class="ml-1"
        ></v-icon>
      </template>
    </v-btn>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, nextTick } from 'vue';

  const expanded = ref(false);
  const innerContentRef = ref<HTMLElement | null>(null);
  const truncatedHeight = ref(150); // Initial truncated height
  const fullHeight = ref(0);

  const contentStyle = computed(() => ({
    maxHeight: expanded.value
      ? `${fullHeight.value}px`
      : `${truncatedHeight.value}px`,
  }));

  const updateHeights = async () => {
    await nextTick();
    if (innerContentRef.value) {
      fullHeight.value = innerContentRef.value.scrollHeight;
    }
  };

  const toggleExpanded = async () => {
    // Update heights before toggling to ensure smooth animation
    await updateHeights();
    expanded.value = !expanded.value;
  };

  onMounted(async () => {
    await updateHeights();
    // Add resize observer to handle dynamic content changes
    if (innerContentRef.value) {
      const observer = new ResizeObserver(async () => {
        await updateHeights();
      });
      observer.observe(innerContentRef.value);
    }
  });
</script>

<style lang="scss" scoped>
  .read-more-wrapper {
    .read-more-text {
      font-size: 14px;
      line-height: 1.8;
      transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;

      &.text-truncated {
        max-height: 150px;
        overflow: hidden;
      }

      &:not(.text-truncated) {
        max-height: 100%;
      }
    }

    .gradient-fade {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 80px;
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0),
        rgba(255, 255, 255, 1)
      );
      opacity: 1;
      transition: opacity 0.3s ease;
      pointer-events: none;

      &.fade-out {
        opacity: 0;
      }
    }

    // Button Styles
    .read-more-btn {
      border-width: 2px !important;
      transition: border-color 0.2s ease-in-out;
      :deep(.v-btn__overlay) {
        display: none;
      }

      :deep(.v-btn__content) {
        .text-secondary {
          color: rgb(var(--v-theme-secondary)) !important;
          font-weight: bold;
          transition: color 0.2s ease-in-out;
        }
      }

      :deep(.v-btn__append) {
        .v-icon {
          transition: color 0.2s ease-in-out;
        }
      }

      &:hover {
        border-color: rgb(var(--v-theme-primary)) !important;
        background-color: rgba(var(--v-theme-primary), 0.05) !important;
        .v-btn__content {
          .text-secondary {
            color: rgb(var(--v-theme-primary)) !important;
          }
        }
        .v-btn__append {
          .v-icon {
            color: rgb(var(--v-theme-primary)) !important;
          }
        }
      }
    }
  }
</style>
